import { policyTypes } from '@ourbranch/policy-types';

export const eligibilityQuestions = [
  {
    id: 'unrepairedDamage',
    question:
      'Is there any unrepaired damage or history of water damage claims on any dwelling or structure on this application?'
  },
  {
    id: 'priorFireLoss',
    question:
      'Has any applicant or household member sustained a prior fire loss, or has there been a fire on any premise in the last 10 years?'
  },
  {
    id: 'priorLiabilityLoss',
    question:
      'Has any applicant or household member sustained a prior liability loss, or had a claim made against them?'
  },
  {
    id: 'chargedWithFelony',
    question: 'Has any applicant or household member ever been charged with a felony?'
  },
  {
    id: 'foreclosureBankruptcy',
    question:
      'Has the applicant had a foreclosure, repossession, bankruptcy, or filed for bankruptcy during the past 5 years?'
  },
  {
    id: 'coverageDeclined',
    question: 'Has any coverage been declined, cancelled or non renewed during the last 3 years?'
  }
];

export const propertyRiskFactorQuestions = [
  {
    id: 'otherResidences',
    question:
      'Does the applicant have any other residences that are not listed on this application, whether owned, occupied, or rented?',
    label: 'Provide address and occupancy details (owned, occupied, or rented) for all offlisted residences'
  },
  {
    id: 'dwellingsForSale',
    question: 'Are there any dwellings or homes for sale that are listed on this application?',
    label: 'Provide addresses of the dwellings or homes for sale, along with any liens related to the sale'
  },
  {
    id: 'unlistedStructures',
    question: 'Are there any structures on the premises that are not listed or that should be excluded?',
    label:
      'Provide details of all unlisted structures on the premises, and list of any structures the applicant wants to exclude with the reason for each exclusion'
  },
  {
    id: 'recreationalVehicles',
    question: 'Are there any recreational vehicles the applicant owns that are not included in this application?',
    tooltip: 'Including but not limited to, snow mobiles, dune buggies, mini bikes, ATVs, etc.',
    label: 'Provide the type, make, models and usage details for all unlisted recreational vehicles'
  },
  {
    id: 'livestockFencing',
    question: 'Is the fencing for livestock on the premises in good repair?',
    tooltip: 'If there are no livestock on the premise, please select "Yes."',
    label: 'Provide a description of condition and type of fencing used for livestock on premises',
    showDetailsWhen: ['false', false]
  },
  {
    id: 'livestockEscape',
    question: 'Has there ever been an incident of livestock escape?',
    tooltip: 'If there are no livestock on the premise, please select "No"',
    label: 'Provide details of each livestock escape incident'
  },
  {
    id: 'solarPanels',
    question: 'Are there any solar panels on the premises?'
  },
  {
    id: 'dogsOrExoticPets',
    question: 'Are there any dogs or exotic pets kept on premises?',
    label:
      'Provide the type of each exotic pet, or if dog, the breed, the bite history, and a brief explanation if there is any history of biting incidents'
  },
  {
    id: 'trampoline',
    question: 'Is there a trampoline on the premises?',
    label:
      'Provide the amount of trampolines, along with details about the presence of safety nets and jump guards for each'
  },
  {
    id: 'dwellingsUnderRenovation',
    question: 'Are there any dwellings/structures under renovation or construction?',
    label: 'Provide details about the dwellings or structures under renovation or construction'
  },
  {
    id: 'farmStructuresRentedToOthers',
    question: 'Are there any farm barns, buildings, or structures on the premises rented to others?',
    label: 'Provide a description of each barn, building, or structure rented to others'
  }
];

export const operationalDetailsQuestions = [
  {
    id: 'businessConductedOnPremises',
    question: 'Is any business conducted on the premises?',
    tooltip: 'Including but not limited to, farming, home office or business, telecommuters, etc.',
    label: 'Provide details about each type of business conducted on the premises'
  },
  {
    id: 'dayCareBusiness',
    question: 'Is there any day care business conducted on the premises?',
    label: 'Provide details related to the day care, and include the number of children'
  },
  {
    id: 'anhydrousAmmoniaEquipment',
    question:
      'Does any insured own anhydrous ammonia or weed spraying equipment for use on the premises or any other property?'
  },
  {
    id: 'processOrManufacture',
    question: 'Does the applicant process or manufacture any of their own products?'
  },
  {
    id: 'huntingOrFishingFee',
    question: 'Does the applicant allow hunting or fishing for a fee on any premises?'
  },
  {
    id: 'customFarming',
    question: 'Does the applicant do any custom farming?',
    label:
      "Provide details related to total acres custom farmed, total receipts, any prior liability claims, and if any custom farming is done outside of the insured's state"
  }
];

// Issue #31439: On a farm endorsement we only re-ask the structure-relevant subset of UW
// questions (the six shown in the issue) — and only when a new dwelling or farm structure is
// added. The applicant-level eligibility questions (felony, bankruptcy, coverage declined,
// prior liability loss) are answered once on the original offer and are never re-asked.
// Composed by id from the full question definitions so the question text/labels/tooltips stay
// in a single source of truth.
const allUWQuestions = [...eligibilityQuestions, ...propertyRiskFactorQuestions, ...operationalDetailsQuestions];

export const ENDORSEMENT_UW_QUESTION_IDS = [
  'unrepairedDamage',
  'priorFireLoss',
  'dwellingsForSale',
  'dwellingsUnderRenovation',
  'farmStructuresRentedToOthers',
  'businessConductedOnPremises'
];

export const endorsementUWQuestions = ENDORSEMENT_UW_QUESTION_IDS.map((id) =>
  allUWQuestions.find((question) => question.id === id)
);

// Fail fast at module load (dev/test) if a source question id is ever renamed/removed out from
// under this list — otherwise an undefined entry would only crash later at user-interaction time.
if (endorsementUWQuestions.some((question) => !question)) {
  const missing = ENDORSEMENT_UW_QUESTION_IDS.filter((id) => !allUWQuestions.some((q) => q.id === id));
  throw new Error(`endorsementUWQuestions: question id(s) not found in source arrays: ${missing.join(', ')}`);
}

// Issues #31456 (Home / Z) and #31507 (Rental / Y): Forreston Home and Rental are subsets of full
// Farm. On a new offer they must not ask the farm-only underwriting questions below. Full Farm (F)
// keeps every question. The "rented to others" question stays for all types but drops the
// "farm barns" phrasing for Home/Rental.

// Livestock/fencing questions — removed for both Home and Rental.
const LIVESTOCK_PROPERTY_RISK_IDS = ['livestockFencing', 'livestockEscape'];
// Property-risk questions removed only for Rental (Y) — extends the Home removal list to also drop
// the recreational vehicle question.
const RENTAL_ONLY_REMOVED_PROPERTY_RISK_IDS = [...LIVESTOCK_PROPERTY_RISK_IDS, 'recreationalVehicles'];
// Operational-detail questions removed for both Home and Rental.
const FARM_ONLY_OPERATIONAL_IDS = ['anhydrousAmmoniaEquipment', 'customFarming'];

// Home/Rental variant of the rented-to-others question — same id (answers round-trip on the same
// field) but without the farm-specific "farm barns" wording.
const rentedToOthersHomeRental = {
  id: 'farmStructuresRentedToOthers',
  question: 'Are there any buildings or structures on the premises rented to others?',
  label: 'Provide a description of each building or structure rented to others'
};

// Fail fast at module load (dev/test) if any id we filter on or override no longer exists in the
// source arrays — mirrors the endorsementUWQuestions guard above so a future question rename can't
// silently produce an empty filter (questions that should be dropped would keep rendering) or a
// no-op rented-to-others override. RENTAL_ONLY_REMOVED_PROPERTY_RISK_IDS is the superset of every
// property-risk id we remove, so checking it covers the Home list too.
const propertyRiskQuestionIds = new Set(propertyRiskFactorQuestions.map((question) => question.id));
const operationalQuestionIds = new Set(operationalDetailsQuestions.map((question) => question.id));
const missingPropertyRiskIds = [...RENTAL_ONLY_REMOVED_PROPERTY_RISK_IDS, rentedToOthersHomeRental.id].filter(
  (id) => !propertyRiskQuestionIds.has(id)
);
const missingOperationalIds = FARM_ONLY_OPERATIONAL_IDS.filter((id) => !operationalQuestionIds.has(id));
if (missingPropertyRiskIds.length || missingOperationalIds.length) {
  throw new Error(
    `getFarmUWQuestionSections: question id(s) not found in source arrays: ${[
      ...missingPropertyRiskIds,
      ...missingOperationalIds
    ].join(', ')}`
  );
}

/**
 * Returns the three UW question sections to render for a given farm policy type. Full Farm (F)
 * returns the original arrays unchanged (same identities). Home (Z) and Rental (Y) drop the
 * farm-only questions per issues #31456 / #31507 and use the de-farmed rented-to-others wording.
 */
export const getFarmUWQuestionSections = (selectedOption) => {
  const isHome = selectedOption === policyTypes.FarmHome;
  const isRental = selectedOption === policyTypes.FarmRental;

  if (!isHome && !isRental) {
    return { eligibilityQuestions, propertyRiskFactorQuestions, operationalDetailsQuestions };
  }

  const removedPropertyRiskIds = isRental ? RENTAL_ONLY_REMOVED_PROPERTY_RISK_IDS : LIVESTOCK_PROPERTY_RISK_IDS;

  return {
    eligibilityQuestions,
    propertyRiskFactorQuestions: propertyRiskFactorQuestions
      .filter((question) => !removedPropertyRiskIds.includes(question.id))
      .map((question) => (question.id === 'farmStructuresRentedToOthers' ? rentedToOthersHomeRental : question)),
    operationalDetailsQuestions: operationalDetailsQuestions.filter(
      (question) => !FARM_ONLY_OPERATIONAL_IDS.includes(question.id)
    )
  };
};

export default {
  eligibilityQuestions,
  propertyRiskFactorQuestions,
  operationalDetailsQuestions,
  endorsementUWQuestions,
  getFarmUWQuestionSections
};
